/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-02T18:00:00+05:00
Intent: Count the AR (Treasure Hunt) campaigns created in the plan's current usage window, so the
usage card and the campaign form see a new AR campaign at once, not only after the backend counts it.
*/
import { Observable, firstValueFrom } from 'rxjs';

/** Campaigns that never took an AR slot: unsent drafts and ones that will never run. */
const NOT_COUNTED = ['draft', 'rejected', 'deleted', 'cancelled', 'canceled'];

const levelType = (level: any): string => {
  const record = level?.level_details ?? level?.level_detail ?? level?.details ?? level;
  return String(record?.level_type ?? record?.type ?? level?.level_type ?? level?.type ?? '').toLowerCase();
};

/** A campaign with at least one AR (geolocation) level. */
export function isArCampaign(contest: any): boolean {
  const levels = contest?._levels_of_contest ?? contest?.levels ?? [];
  return Array.isArray(levels) && levels.some(level => levelType(level) === 'geolocation');
}

/** Epoch ms of a date the gateway sends as epoch ms, a numeric string or an ISO string. */
function toTime(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  const time = typeof value === 'number' || /^\d+$/.test(String(value)) ? Number(value) : Date.parse(String(value));
  return Number.isFinite(time) ? time : null;
}

/** Start of the plan's AR usage window: the last reset, else the subscription start. */
export function arUsageWindowStart(plan: any): number | null {
  return toTime(plan?.usage_window_start) ?? toTime(plan?.ar_usage_window_start) ?? toTime(plan?.start_date);
}

/** Whether the campaign uses one of the plan's AR campaigns this window. */
export function countsTowardArUsage(contest: any, windowStart: number | null): boolean {
  if (!isArCampaign(contest)) return false;
  const status = String(contest?.status ?? '').toLowerCase();
  const review = String(contest?.review_status ?? '').toLowerCase();
  if (NOT_COUNTED.includes(status) || NOT_COUNTED.includes(review)) return false;
  const created = toTime(contest?.created_at);
  return windowStart === null || created === null || created >= windowStart;
}

/** The AR campaigns created this window, over every page of the campaign list. */
export async function countArCampaignsInWindow(
  pagesService: { getAll(key: string, params?: any): Observable<any> },
  windowStart: number | null,
): Promise<number> {
  let count = 0;
  for (let page = 1; page <= 50; page++) {
    const response: any = await firstValueFrom(pagesService.getAll('TEST_PAGINATION', {
      page, per_page: 100, status: ''
    }));
    if (!Array.isArray(response?.items)) throw new Error('Campaign list unavailable');
    count += response.items.filter((contest: any) => countsTowardArUsage(contest, windowStart)).length;
    const totalPages = Number(response?.meta?.total_pages) || 1;
    if (page >= totalPages || response.items.length === 0) break;
  }
  return count;
}

/**
 * AR campaigns used this window. The plan's `ar_campaigns_used` only counts a campaign once the
 * backend does (a just-submitted one waiting for review is not in it yet), so the campaigns
 * themselves are counted too and the higher number wins. Unlimited plans and plans without AR
 * keep the plan's number; so does a failed count.
 */
export async function effectiveArCampaignsUsed(
  pagesService: { getAll(key: string, params?: any): Observable<any> },
  plan: any,
  limited: boolean,
): Promise<number> {
  const reported = Number(plan?.ar_campaigns_used) || 0;
  if (!limited) return reported;
  try {
    return Math.max(reported, await countArCampaignsInWindow(pagesService, arUsageWindowStart(plan)));
  } catch {
    return reported;
  }
}
